import React, { type ReactNode, type JSX } from "react";
import { twMerge, twJoin } from "tailwind-merge";

import { SupText } from "../../../../components/UI/Text";

export interface Option<V> {
  label: string | JSX.Element;
  value: V;
}

export interface OptionProps<V> {
  value: V;
  label: string | JSX.Element;
  active?: boolean;
  className?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  onSelect?: (value: V) => void;
  renderLabel?: (option: Option<V>, active: boolean) => ReactNode;
  renderIcon?: (option: Option<V>, active: boolean) => ReactNode;
}

export function defaultLabelRenderer<V>({ label }: Option<V>) {
  return typeof label === "string" ? <SupText variant="subtitle1">{label}</SupText> : label;
}

const colorStyles =
  "text-neutral-800 transition-colors duration-200 hover:bg-primary-50 hover:text-neutral-900 focus:bg-primary-100 focus:text-neutral-800 active:bg-primary-100 active:text-neutral-900 [&.active]:bg-primary-200 [&.active]:text-neutral-900 [&.disabled]:bg-neutral-25 [&.disabled]:text-neutral-600";

export function SupOption<V>({
  className,
  value,
  label,
  active = false,
  icon,
  disabled,
  onSelect,
  renderLabel = defaultLabelRenderer<V>,
  renderIcon = () => icon,
}: OptionProps<V>) {
  const option = { label, value };
  const optionIcon = renderIcon(option, active);

  return (
    <div
      tabIndex={0}
      className={twMerge(
        "sup-select-option flex items-center gap-2 h-[3.25rem] px-2 cursor-pointer",
        disabled && "disabled cursor-auto pointer-events-none",
        active && "active cursor-auto pointer-events-none",
        colorStyles,
        className
      )}
      onClick={() => onSelect?.(option.value)}
    >
      {Boolean(optionIcon) && <span className={twJoin("shrink-0", disabled ? "opacity-16" : "")}>{renderIcon(option, active)}</span>}
      <span className={twJoin("flex-1", disabled ? "opacity-44" : "")}>{renderLabel(option, active)}</span>
    </div>
  );
}
